import { mainApi } from '@/lib/api';
import {
    AffiliateFiltersPayload,
    AffiliateMetrics,
    AffiliateResponse,
    EmailTemplate,
} from './types/affiliateAnalytics.types';

/**
 * Fetch affiliates list with pagination and filters
 */
export async function fetchAffiliates(
    id: string,
    params: AffiliateFiltersPayload,
    signal?: AbortSignal
): Promise<AffiliateResponse> {
    const { data } = await mainApi.get<AffiliateResponse>(`v1/resell_rights/resellers/${id}`, { params, signal });
    return data;
}

/**
 * Fetch affiliate metrics (total earnings, sales, resellers)
 */
export async function fetchAffiliateMetrics(id: string, signal?: AbortSignal): Promise<AffiliateMetrics> {
    const { data } = await mainApi.get<AffiliateMetrics>(`v1/resell_rights/resellers/${id}/metrics`, { signal });
    return data;
}

/**
 * Fetch email template variables for mass email
 */
export async function fetchEmailTemplate(): Promise<EmailTemplate> {
    const { data } = await mainApi.get<{ affiliate_mass_email: EmailTemplate }>(
        'v1/courses/email-template-variables/affiliate-mass-email'
    );
    return data.affiliate_mass_email;
}

/**
 * Send mass email to affiliates
 */
export async function sendMassEmail(
    id: string,
    payload: {
        params: { filter_array: string; timezone: string };
        subject: string;
        body: string;
    }
): Promise<void> {
    await mainApi.post(`v1/resell_rights/resellers/${id}/mass-email`, payload);
}

/**
 * Revoke affiliate access
 */
export async function revokeAffiliateAccess(id: string, resellerId: number): Promise<void> {
    await mainApi.post(`v1/resell_rights/resellers/${id}/${resellerId}/revoke`);
}

/**
 * Resend affiliate link
 */
export async function resendAffiliateLink(id: string, resellerId: number): Promise<void> {
    await mainApi.post(`v1/resell_rights/resellers/${id}/${resellerId}/resend-link`);
}

/**
 * Add affiliate manually
 */
export async function addAffiliate(payload: { full_name: string; email: string; page_id: string }): Promise<void> {
    await mainApi.post('/v1/resell-rights/add-affiliate', payload);
}

/**
 * Update affiliate commission percentage
 */
export async function updateAffiliateCommission(payload: {
    affiliate_custom_percentage: number;
    page_id: string;
    affiliate_user_id: number;
}): Promise<void> {
    await mainApi.post('v1/resell-rights/update-commission', payload);
}

/**
 * Remove custom affiliate commission (reset to default)
 */
export async function removeAffiliateCommission(payload: {
    page_id: string;
    affiliate_user_id: number;
}): Promise<void> {
    await mainApi.post('v1/resell-rights/remove-custom-commission', payload);
}
